import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, Settings, Trash2, PlusCircle, Eye, EyeOff, Info, HelpCircle, Plus, Layers, ChevronRight, ChevronDown, X, Lock, Unlock, MessageSquare, Sparkles, Mail, MessageCircle, PhoneForwarded, Webhook, ListFilter, Building, Calendar, Clock, Maximize2, Link, Code, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import RichTextEditor from '../../components/ui/RichTextEditor'; // Using RichTextEditor
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from "../../components/ui/select";
import { cn } from '../../lib/utils';
import OrderPathwayPreview from './OrderPathwayPreview';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ConnectionSelectionModal from './ConnectionSelectionModal';
import { getConnectionPresets, fetchConnectionActionFields } from '../../services/connectionService';
import { getSupabaseClient } from '../../supabaseClient';
import NewCategoryModal from '../../brain/NewCategoryModal';
import SimpleActionToolModal from '../call-actions/SimpleActionToolModal';
import EmailAddressInput from '../EmailAddressInput';
import ApiHeadersInput from '../ApiHeadersInput';
import PhoneNumberInput from '../PhoneNumberInput';

// Helper function to calculate effective SMS length
const calculateEffectiveSmsLength = (htmlString) => {
  if (!htmlString) return 0;
  // This function relies on DOM APIs, ensure it runs client-side.
  if (typeof document === 'undefined') return 0; 

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  const mentionPills = tempDiv.querySelectorAll('span.mention-pill');
  const numMentions = mentionPills.length;

  // Clone the div to manipulate for text extraction
  const textExtractionDiv = tempDiv.cloneNode(true);
  // Remove mention pills to get the length of the text outside them
  textExtractionDiv.querySelectorAll('span.mention-pill').forEach(pill => pill.remove());
  
  const textWithoutMentions = textExtractionDiv.textContent || '';
  
  return textWithoutMentions.length + (numMentions * 25);
};

// Move GeneralSettingsTab outside the main component to prevent recreation on each render
const GeneralSettingsTab = ({ 
  pathwayConfig, 
  setPathwayConfig, // This will be the updateState function from parent
  questions, // <<< ADDED: Accept questions prop
  validationErrors, 
  locations, 
  showLocationVariants, 
  setShowLocationVariants, 
  setHasUnsavedChanges
}) => {
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = useState(false);
  const supabase = getSupabaseClient();
  
  // Fetch categories when component mounts
  useEffect(() => {
    fetchCategories();
  }, []);
  
  // Fetch categories from the database
  const fetchCategories = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('brain_categories')
        .select('id, name')
        .eq('account_id', user.id)
        .order('name');
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };
  
  // Handle category selection - Use the passed update function
  const handleCategoryChange = (value) => {
    if (value === 'new') {
      setIsNewCategoryModalOpen(true);
      return;
    }
    setPathwayConfig('category_id', value); // Call updateState with (name, value)
  };
  
  // Handle new category creation - Use the passed update function
  const handleNewCategoryCreated = (newCategory) => {
    setCategories(prev => [...prev, newCategory]);
    setPathwayConfig('category_id', newCategory.id); // Call updateState with (name, value)
    setIsNewCategoryModalOpen(false);
  };
  
  // Handle connection selection from the modal - Use the passed update function
  const handleSelectConnection = (connection) => {
    // console.log('Selected connection:', connection);
    // Update toolConnection fields via the updateState function
    setPathwayConfig('toolConnection.endpointType', connection.type || 'custom');
    setPathwayConfig('toolConnection.connectionId', connection.id);
    setPathwayConfig('toolConnection.connectionName', connection.name);
    // Clear action if connection changes?
    setPathwayConfig('toolConnection.actionId', null);
    setPathwayConfig('toolConnection.actionName', null);
    setHasUnsavedChanges(true);
  };
  
  // Handle connection action selection from the modal - Use the passed update function
  const handleSelectConnectionAction = async (action) => {
    // console.log('Selected action:', action);
    const fields = await fetchConnectionActionFields(action.id);
    const mapType = (dbType) => ({ 'string': 'text', 'numeric': 'numeric', 'boolean': 'checkbox', 'enum': 'radio' }[dbType] || 'text');
    // Ensure fields are sorted by the 'order' property from the backend first
    const sorted = fields.sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
    const orderQs = []; const qualQs = []; const parameters = [];
    
    // Keep track of dbOrder for qualifying and non-qualifying separately
    let currentQualifyingDbOrder = 0;
    let currentOrderDbOrder = 0;

    sorted.forEach((f) => { // Removed idx, use f.order for dbOrder primarily
      const uiType = mapType(f.type);
      const isQualifyingField = f.is_qualifying || false;
      
      const qObj = { 
        id: `field_${f.id || Math.random().toString(36).substring(7)}`, // Prefer f.id if it's unique and stable
        type: uiType, 
        text: f.question, 
        required: true, 
        isEditable: f.is_editable ?? true, // Default to true if undefined
        // TODO: Populate options if f.type is 'enum' and enums are available for f.id
        options: uiType === 'radio' ? (f.enum_options || []) : [], // Assuming enum_options might come with the field
        isQualifying: isQualifyingField,
        // Assign dbOrder based on backend's order field, or sequential if not available
        dbOrder: f.order ?? (isQualifyingField ? currentQualifyingDbOrder++ : currentOrderDbOrder++),
        parentFieldId: f.parent_field_id || null, // If your actions can define parent/child
        criteria: f.criteria || null, // If your actions can define criteria
      };
      parameters.push({ param: f.parameter, questionId: qObj.id });
      if (isQualifyingField) {
        qualQs.push(qObj);
      } else {
        orderQs.push(qObj);
      }
    });

    // Update multiple fields using the updateState function
    setPathwayConfig('toolConnection.actionId', action.id);
    setPathwayConfig('toolConnection.actionName', action.name);
    setPathwayConfig('toolConnection.endpoint', action.endpoint || '');
    setPathwayConfig('toolConnection.parameters', parameters);
    setPathwayConfig('toolConnection.canChangeQualifying', action.can_change_qualifying);
    setPathwayConfig('toolConnection.canAddQuestions', action.can_add_questions);
    
    // Ensure questions are sorted by their newly assigned dbOrder before setting them
    qualQs.sort((a,b) => (a.dbOrder || 0) - (b.dbOrder || 0));
    orderQs.sort((a,b) => (a.dbOrder || 0) - (b.dbOrder || 0)); // Primarily for top-level if hierarchy isn't deep from action

    setPathwayConfig('orderQuestions', orderQs);
    setPathwayConfig('qualifyingQuestions', qualQs);
    setHasUnsavedChanges(true);
  };
  
  // Create a new connection manually - Use the passed update function
  const handleCreateConnection = (newConnection) => {
    // console.log('Created new connection:', newConnection);
    setPathwayConfig('toolConnection.endpointType', newConnection.type || 'custom');
    setPathwayConfig('toolConnection.connectionId', newConnection.id);
    setPathwayConfig('toolConnection.connectionName', newConnection.name);
    setPathwayConfig('toolConnection.endpoint', newConnection.endpoint || '');
    setPathwayConfig('toolConnection.apiKey', newConnection.apiKey);
    setPathwayConfig('toolConnection.actionId', null);
    setPathwayConfig('toolConnection.actionName', null);
    setHasUnsavedChanges(true);
  };
  
  // Generic handler for simple input changes - Use the passed update function
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPathwayConfig(name, value); // Call updateState with (name, value)
  };

  const hasPredefinedConnection = pathwayConfig?.toolConnection?.connectionId;
                                 
  return (
  <motion.div
    initial={{ opacity: 0, y: 5 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="space-y-4 sm:space-y-6 py-3 sm:py-4"
  >
    {/* Name Field */}
    <div className="space-y-1.5 sm:space-y-2">
      <Label htmlFor="name" className="text-xs sm:text-sm font-medium flex items-center">
        Pathway Name <span className="text-red-500 ml-0.5">*</span>
      </Label>
      <Input
        id="name"
        name="name"
        value={pathwayConfig.name || ''}
        onChange={handleChange} // Use generic handler
        placeholder="Enter a descriptive name"
        className={cn(
          "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
          validationErrors?.name && "border-red-300 focus:border-red-500 focus:ring-red-500"
        )}
      />
        {validationErrors?.name && (
        <motion.p 
          className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
        >
          {validationErrors.name}
        </motion.p>
      )}
    </div>
    
    {/* Description Field */}
    <div className="space-y-1.5 sm:space-y-2">
      <Label htmlFor="description" className="text-xs sm:text-sm font-medium flex items-center">
        Description <span className="text-gray-400 ml-1 text-[10px] sm:text-xs font-normal">(Optional)</span>
      </Label>
      <RichTextEditor
        value={pathwayConfig.description || ''}
        onChange={(htmlContent) => setPathwayConfig('description', htmlContent)} // Ensure updateState/setPathwayConfig is called correctly
        placeholder="Add a brief description about this order pathway (type '@' to mention questions)"
        questions={questions} // Pass the questions for mention suggestions
        className="min-h-[70px] sm:min-h-[80px]" // Apply similar sizing, actual styling is within RichTextEditor
      />
    </div>
    
    {/* Category Dropdown */}
    <div className="space-y-1.5 sm:space-y-2">
      <Label htmlFor="category" className="text-xs sm:text-sm font-medium flex items-center">
        Category <span className="text-red-500 ml-0.5">*</span>
      </Label>
      <Select 
        value={pathwayConfig.category_id || ''}
        onValueChange={handleCategoryChange} // Use specific handler
      >
        <SelectTrigger
          id="category"
          className={cn(
            "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
            validationErrors?.category_id && "border-red-300 focus:border-red-500 focus:ring-red-500"
          )}
        >
          <SelectValue placeholder="Select a category" />
        </SelectTrigger>
        {/* Apply z-index here */}
        <SelectContent className="z-[10001]">
          {categories.map(category => (
            <SelectItem key={category.id} value={category.id} className="text-sm">
              {category.name}
            </SelectItem>
          ))}
          <SelectItem value="new" className="text-sm text-blue-600">
            <div className="flex items-center">
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Create new category
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
        
        {validationErrors?.category_id && (
        <motion.p 
          className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
        >
          {validationErrors.category_id}
        </motion.p>
      )}
    </div>
    
    {/* Connection Section - Only show when connectionEnabled is true */}
    {pathwayConfig.connectionEnabled && (
      <div className="space-y-4 mt-4">
        <Label className="text-xs sm:text-sm font-medium">Connection</Label>
        
        {/* Show connection info if a connection is selected */}
        {hasPredefinedConnection ? (
          <div className="border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-50 rounded-md text-blue-600">
                  <Layers className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-medium">{pathwayConfig.toolConnection.connectionName}</h3>
                  {pathwayConfig.toolConnection.actionName && (
                    <p className="text-sm text-gray-500">
                      Action: {pathwayConfig.toolConnection.actionName}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowConnectionModal(true)}
              >
                Change
              </Button>
            </div>
          </div>
        ) : (
          <Button 
            variant="outline" 
            className="w-full py-8 border-dashed"
            onClick={() => setShowConnectionModal(true)}
          >
            <Plus className="h-5 w-5 mr-2" />
            Select Connection
          </Button>
        )}
      </div>
    )}
      
    {/* Connection Selection Modal */}
    <ConnectionSelectionModal 
      isOpen={showConnectionModal}
      onClose={() => setShowConnectionModal(false)}
      onSelectConnection={handleSelectConnection}
      onSelectConnectionAction={handleSelectConnectionAction}
      onCreateConnection={handleCreateConnection}
      selectedConnection={hasPredefinedConnection ? {
        id: pathwayConfig.toolConnection.connectionId,
        name: pathwayConfig.toolConnection.connectionName
      } : null}
    />
    
    {/* New Category Modal */}
    <NewCategoryModal
      isOpen={isNewCategoryModalOpen}
      onClose={() => setIsNewCategoryModalOpen(false)}
      onSave={handleNewCategoryCreated}
    />

    {/* Location Variants Toggle - Moved below Connection section, only show if multiple locations exist */}
    {locations && locations.length > 1 && (
      <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100">
        <div className="space-y-0.5 sm:space-y-1">
          <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
            <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 text-gray-500" />
            Enable Location Variants
          </h4>
          <p className="text-[10px] sm:text-xs text-gray-500">
            Create different versions of this pathway for specific locations
          </p>
        </div>
        <Switch
          checked={showLocationVariants}
          onCheckedChange={(checked) => {
            setShowLocationVariants(checked); // Directly set show state
            setHasUnsavedChanges(true);
          }}
          className="data-[state=checked]:bg-blue-500"
        />
      </div>
    )}
  </motion.div>
);
};

// Move QuestionsTab outside the main component to prevent recreation on each render
export const QuestionsTab = ({ 
  pathwayConfig, 
  setPathwayConfig, 
  showLocationVariants, // Added
  handleFormChangeWithVariants, // Added
  setHasUnsavedChanges, // Added
  onExpandClick // <-- NEW PROP for expand functionality
}) => {
  const [questionType, setQuestionType] = useState('text');
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [isQualifying, setIsQualifying] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isActionSelectorModalOpen, setIsActionSelectorModalOpen] = useState(false);
  
  // --- State for Conditional Questions (Refactored) ---
  const [isDependentQuestion, setIsDependentQuestion] = useState(false);
  const [parentQuestionId, setParentQuestionId] = useState('');
  const [selectedParentQuestionDetails, setSelectedParentQuestionDetails] = useState(null); // To store { id, type, options, text }
  // parentQuestionTriggerRule: { type: 'answered' | 'not_answered' | 'matches_specific_value', value?: any (string, array, string for 'yes'/'no') }
  const [parentQuestionTriggerRule, setParentQuestionTriggerRule] = useState({ type: 'answered' }); 
  // --- END State (Refactored) ---
  
  // Check if questions can be added based on the selected action
  const canAddQuestions = !pathwayConfig.toolConnection?.actionId || 
    pathwayConfig.toolConnection?.canAddQuestions !== false;
  
  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // --- NEW: Effect to update selectedParentQuestionDetails and reset trigger rule ---
  useEffect(() => {
    if (parentQuestionId) {
      const allQs = getAllQuestions(); // Ensure getAllQuestions is available and correct
      const parentQ = allQs.find(q => q.id === parentQuestionId);
      setSelectedParentQuestionDetails(parentQ ? { id: parentQ.id, type: parentQ.type, options: parentQ.options || [], text: parentQ.text } : null);
      // Reset rule when parent changes, default to 'answered'
      setParentQuestionTriggerRule({ type: 'answered', value: undefined }); 
    } else {
      setSelectedParentQuestionDetails(null);
      setParentQuestionTriggerRule({ type: 'answered', value: undefined });
    }
    // Rerun if questions themselves change, as parentQ details might become stale if parent is edited/deleted
  }, [parentQuestionId, pathwayConfig.qualifyingQuestions, pathwayConfig.orderQuestions]); 
  // --- END NEW Effect ---
  
  // Combine qualifying and order questions for display
  const getAllQuestions = () => {
    const qualifyingWithMeta = (pathwayConfig.qualifyingQuestions || []).map((q, idx) => ({
      ...q,
      // originalIndex: idx, // Retain if needed for direct array manipulation elsewhere, but dbOrder is prime for sorting
      depth: 0 
    }));
    const orderWithMeta = (pathwayConfig.orderQuestions || []).map((q, idx) => ({
      ...q,
      // originalIndex: idx, 
      depth: 0 
    }));
    // IMPORTANT: The questions from pathwayConfig (qualifying, order) should ideally already be
    // somewhat sorted by their dbOrder for top-level items due to initializePathway.
    // However, explicit sorting using dbOrder in getHierarchicalQuestionsForDisplay is more robust.
    return [...qualifyingWithMeta, ...orderWithMeta];
  };
  
  // --- NEW: Function to get questions in a hierarchical structure for display ---
  const getHierarchicalQuestionsForDisplay = () => {
    const allQuestionsFromConfig = getAllQuestions(); 
    const questionMap = new Map(allQuestionsFromConfig.map(q => [q.id, { ...q, children: [] }]));
    const topLevelQuestions = [];

    // Build the tree structure
    for (const question of allQuestionsFromConfig) {
      const mappedQuestion = questionMap.get(question.id);
      if (question.parentFieldId && questionMap.has(question.parentFieldId)) {
        const parent = questionMap.get(question.parentFieldId);
        if (parent) {
          parent.children.push(mappedQuestion);
        }
      } else {
        topLevelQuestions.push(mappedQuestion);
      }
    }

    // Flatten the structure for rendering, adding depth and sorting by dbOrder
    const displayList = [];
    let globalRenderIndex = 0;

    function flatten(questionsToFlatten, depth) {
      // Sort current level questions (top-level or siblings) by their dbOrder
      questionsToFlatten.sort((a, b) => (a.dbOrder || 0) - (b.dbOrder || 0));

      for (const question of questionsToFlatten) {
        displayList.push({ 
          ...question, 
          depth, 
          renderIndex: globalRenderIndex++, 
          // Ensure originalIndex for editing/deleting refers to its position in the *config* arrays
          // This requires finding it in the original flat lists if pathwayConfig lists are not pre-sorted exactly as display requires.
          // For simplicity, we assume `editQuestion` and `deleteQuestion` will primarily use `question.id` 
          // or can re-find the item in pathwayConfig.qualifyingQuestions/orderQuestions using its `id`.
          // Let's add `originalConfigArrayIndex` and `isQualifyingFromConfig` for robustness if needed by edit/delete.
          originalConfigArrayIndex: question.isQualifying 
            ? (pathwayConfig.qualifyingQuestions || []).findIndex(q => q.id === question.id)
            : (pathwayConfig.orderQuestions || []).findIndex(q => q.id === question.id),
          isQualifyingFromConfig: question.isQualifying
        });
        if (question.children.length > 0) {
          // Children are already part of `question.children` and will be sorted in the recursive call
          flatten(question.children, depth + 1);
        }
      }
    }
    // Sort top-level qualifying questions first, then top-level order questions, then by dbOrder.
    const sortedTopLevelQuestions = topLevelQuestions.sort((a,b) => {
        if (a.isQualifying && !b.isQualifying) return -1;
        if (!a.isQualifying && b.isQualifying) return 1;
        return (a.dbOrder || 0) - (b.dbOrder || 0);
    });

    flatten(sortedTopLevelQuestions, 0);
    return displayList;
  };
  // --- END NEW Hierarchical Function ---
  
  const addOption = () => {
    if (newOption.trim()) {
      setOptions([...options, newOption.trim()]);
      setNewOption('');
      setHasUnsavedChanges(true); // Mark change
    }
  };

  const updatePathwayConfig = (update) => {
      if (showLocationVariants) {
          // For variants, updates are handled differently, 
          // typically by passing field name and value to the variant handler.
          // Here, we need to update the entire array, so we pass the field name and the new array.
          if (update.qualifyingQuestions) {
              handleFormChangeWithVariants('qualifyingQuestions', update.qualifyingQuestions);
          } else if (update.orderQuestions) {
              handleFormChangeWithVariants('orderQuestions', update.orderQuestions);
          } else {
              // Handle other potential direct updates if necessary
              // This might need refinement based on how other fields are structured
              Object.keys(update).forEach(key => {
                 handleFormChangeWithVariants(key, update[key]);
              });
          }
      } else {
          // Original logic for non-variant mode
          setPathwayConfig(prev => ({ ...prev, ...update }));
      }
      setHasUnsavedChanges(true);
  };
  
  const saveQuestion = () => {
    if (!questionText.trim()) {
      toast.error('Please enter question text');
      return;
    }
    
    const questionIdToUse = editingIndex !== null
      ? (isQualifying ? pathwayConfig.qualifyingQuestions[editingIndex].id : pathwayConfig.orderQuestions[editingIndex].id)
      : `temp_${generateId()}`;
    
    let question = {
      id: questionIdToUse,
      type: questionType,
      text: questionText,
      required: true,
      options: (questionType === 'radio' || questionType === 'select') ? options : [], // Ensure select is also handled
      isEditable: true,
      // isQualifying will be set below for new questions, or derived for existing
    };
    
    // --- NEW: Add conditional properties if applicable (Refactored & Corrected) ---
    if (isDependentQuestion && parentQuestionId && parentQuestionTriggerRule && selectedParentQuestionDetails) {
      question.parentFieldId = parentQuestionId;
      // ... (your existing logic for dbCondition, dbValue, dbLogic)
      // Ensure this section correctly populates question.criteria
      // For example:
      let criteria = { type: 'AND', triggers: [] };
      let dbCondition = '';
      let dbValue = parentQuestionTriggerRule.value;
      let dbLogic = 'AND'; // Default logic, can be part of criteria structure

      switch (parentQuestionTriggerRule.type) {
        case 'answered':
          dbCondition = 'answered';
          dbValue = undefined;
          break;
        case 'not_answered':
          dbCondition = 'not_answered';
          dbValue = undefined;
          break;
        case 'matches_specific_value':
          switch (selectedParentQuestionDetails.type) {
            case 'radio':
            case 'select':
              dbCondition = 'equals_any_of';
              dbLogic = 'OR';
              dbValue = Array.isArray(parentQuestionTriggerRule.value) ? parentQuestionTriggerRule.value.filter(v => typeof v === 'string') : [];
              if (dbValue.length === 0) { /* Consider default behavior */ }
              break;
            case 'checkbox':
              dbCondition = 'equals';
              dbValue = typeof parentQuestionTriggerRule.value === 'string' ? parentQuestionTriggerRule.value.toLowerCase() : undefined;
              if (dbValue !== 'yes' && dbValue !== 'no') dbValue = undefined;
              break;
            default:
              dbCondition = 'equals';
              dbValue = typeof parentQuestionTriggerRule.value === 'string' ? parentQuestionTriggerRule.value : undefined;
              break;
          }
          break;
        default:
          // Handle unknown trigger rule type if necessary
          break;
      }
      if (dbCondition) {
          criteria.triggers.push({
              field_id: parentQuestionId, // or the parameter name if that's what your backend expects
              condition: dbCondition,
              value: dbValue,
              logic: dbLogic
          });
      }
      question.criteria = criteria.triggers.length > 0 ? criteria : null;
    }
    // --- END Conditional Properties ---

    let update = {};

    if (editingIndex !== null) {
      // Update existing question
      const originalQuestion = isQualifying
        ? pathwayConfig.qualifyingQuestions[editingIndex]
        : pathwayConfig.orderQuestions[editingIndex];
      question.dbOrder = originalQuestion.dbOrder; // Preserve existing dbOrder
      question.isQualifying = isQualifying; // Update qualifying status

      if (isQualifying) {
        const updatedQuestions = [...(pathwayConfig.qualifyingQuestions || [])];
        updatedQuestions[editingIndex] = question;
        update = { qualifyingQuestions: updatedQuestions };
        toast.success('Qualifying question updated');
      } else {
        const updatedQuestions = [...(pathwayConfig.orderQuestions || [])];
        updatedQuestions[editingIndex] = question;
        update = { orderQuestions: updatedQuestions };
        toast.success('Order question updated');
      }
      setEditingIndex(null);
    } else {
      // Add new question
      question.isQualifying = isQualifying; // Set qualifying status for the new question object

      if (isQualifying) {
        const qualQs = pathwayConfig.qualifyingQuestions || [];
        question.dbOrder = qualQs.length > 0 ? Math.max(-1, ...qualQs.map(q => q.dbOrder || -1)) + 1 : 0;
        update = {
          qualifyingQuestions: [...qualQs, question]
        };
        toast.success('Qualifying question added');
      } else {
        const orderQs = pathwayConfig.orderQuestions || [];
        if (question.parentFieldId) { // Dependent question
          const siblings = orderQs.filter(q => q.parentFieldId === question.parentFieldId);
          question.dbOrder = siblings.length > 0 ? Math.max(-1, ...siblings.map(q => q.dbOrder || -1)) + 1 : 0;
        } else { // Top-level order question
          const topLevelOrderQs = orderQs.filter(q => !q.parentFieldId);
          question.dbOrder = topLevelOrderQs.length > 0 ? Math.max(-1, ...topLevelOrderQs.map(q => q.dbOrder || -1)) + 1 : 0;
        }
        update = {
          orderQuestions: [...orderQs, question]
        };
        toast.success('Order question added');
      }
    }
    
    updatePathwayConfig(update);
    
    // Reset form
    setQuestionType('text');
    setQuestionText('');
    setOptions([]);
    setIsQualifying(false);
    setIsDependentQuestion(false);
    setParentQuestionId(''); 
    // setParentQuestionTriggerRule({ type: 'answered', value: undefined }); // Reset by useEffect
  };
  
  const editQuestion = (question) => { // Removed index parameter
    setQuestionType(question.type);
    setQuestionText(question.text);
    setOptions(question.options || []);
    // Use question.originalConfigArrayIndex to set editingIndex
    setEditingIndex(question.originalConfigArrayIndex); 
    setIsQualifying(question.isQualifyingFromConfig); // Use isQualifyingFromConfig
    // --- NEW: Populate conditional question state if editing ---
    if (question.parentFieldId) {
      setIsDependentQuestion(true);
      setParentQuestionId(question.parentFieldId);
      // Parse criteria (assuming simple structure for now)
      if (question.criteria && question.criteria.triggers && question.criteria.triggers.length > 0) {
        const critTrigger = question.criteria.triggers[0];
        let newRuleType = 'answered'; // Default
        let newRuleValue = critTrigger.value;

        // Determine newRuleType based on critTrigger.condition and parent question type (which will be set by useEffect)
        if (critTrigger.condition === 'answered' || critTrigger.condition === 'not_answered') {
          newRuleType = critTrigger.condition;
          newRuleValue = undefined;
        } else if (critTrigger.condition === 'equals' || critTrigger.condition === 'equals_any_of') {
          newRuleType = 'matches_specific_value';
          // newRuleValue is already critTrigger.value - the UI will adapt based on parent type
        } else {
          // Fallback for other unknown conditions from older data, or if logic changes
          newRuleType = 'matches_specific_value'; 
        }
        setParentQuestionTriggerRule({ type: newRuleType, value: newRuleValue });
      } else {
        // Default if criteria is missing or malformed
        setParentQuestionTriggerRule({ type: 'answered', value: undefined }); 
      }
    } else {
      setIsDependentQuestion(false);
      setParentQuestionId(''); // Will trigger useEffect to reset details and rule
      // setParentQuestionTriggerRule({ type: 'answered', value: undefined }); // Reset by useEffect
    }
    // --- END NEW Populate ---
  };
  
  const deleteQuestion = (question) => { // Removed index parameter
    let update = {};
    // Use question.isQualifyingFromConfig and question.originalConfigArrayIndex
    if (question.isQualifyingFromConfig) {
      const updatedQuestions = (pathwayConfig.qualifyingQuestions || []).filter((_, i) => i !== question.originalConfigArrayIndex);
      update = { qualifyingQuestions: updatedQuestions };
      toast.success('Qualifying question deleted');
    } else {
      const updatedQuestions = (pathwayConfig.orderQuestions || []).filter((_, i) => i !== question.originalConfigArrayIndex);
      update = { orderQuestions: updatedQuestions };
      toast.success('Order question deleted');
    }
    updatePathwayConfig(update);
  };
  
  const moveQuestion = (questionToMove, direction) => {
    const { qualifyingQuestions = [], orderQuestions = [] } = pathwayConfig;
    let newQualifyingQuestions = [...qualifyingQuestions];
    let newOrderQuestions = [...orderQuestions];
    let itemMoved = false;

    // Use isQualifyingFromConfig if available, otherwise fallback to isQualifying
    const isMovingQualifying = questionToMove.isQualifyingFromConfig !== undefined 
                               ? questionToMove.isQualifyingFromConfig 
                               : questionToMove.isQualifying;

    if (isMovingQualifying) {
      const sortedQualifying = [...newQualifyingQuestions].sort((a, b) => (a.dbOrder || 0) - (b.dbOrder || 0));
      const currentIndex = sortedQualifying.findIndex(q => q.id === questionToMove.id);

      if (currentIndex === -1) return;
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex >= 0 && targetIndex < sortedQualifying.length) {
        const question1InSorted = sortedQualifying[currentIndex];
        const question2InSorted = sortedQualifying[targetIndex];

        const q1OrigIndex = newQualifyingQuestions.findIndex(q => q.id === question1InSorted.id);
        const q2OrigIndex = newQualifyingQuestions.findIndex(q => q.id === question2InSorted.id);

        if (q1OrigIndex !== -1 && q2OrigIndex !== -1) {
          const tempDbOrder = newQualifyingQuestions[q1OrigIndex].dbOrder;
          newQualifyingQuestions[q1OrigIndex] = { ...newQualifyingQuestions[q1OrigIndex], dbOrder: newQualifyingQuestions[q2OrigIndex].dbOrder };
          newQualifyingQuestions[q2OrigIndex] = { ...newQualifyingQuestions[q2OrigIndex], dbOrder: tempDbOrder };
          itemMoved = true;
          updatePathwayConfig({ qualifyingQuestions: newQualifyingQuestions });
        }
      }
    } else {
      const parentId = questionToMove.parentFieldId;
      const siblings = newOrderQuestions
        .filter(q => q.parentFieldId === parentId)
        .sort((a, b) => (a.dbOrder || 0) - (b.dbOrder || 0));

      const currentIndexInSiblings = siblings.findIndex(q => q.id === questionToMove.id);
      if (currentIndexInSiblings === -1) return;

      const targetIndexInSiblings = direction === 'up' ? currentIndexInSiblings - 1 : currentIndexInSiblings + 1;

      if (targetIndexInSiblings >= 0 && targetIndexInSiblings < siblings.length) {
        const question1InSiblings = siblings[currentIndexInSiblings];
        const question2InSiblings = siblings[targetIndexInSiblings];

        const q1OrigIndex = newOrderQuestions.findIndex(q => q.id === question1InSiblings.id);
        const q2OrigIndex = newOrderQuestions.findIndex(q => q.id === question2InSiblings.id);

        if (q1OrigIndex !== -1 && q2OrigIndex !== -1) {
          const tempDbOrder = newOrderQuestions[q1OrigIndex].dbOrder;
          newOrderQuestions[q1OrigIndex] = { ...newOrderQuestions[q1OrigIndex], dbOrder: newOrderQuestions[q2OrigIndex].dbOrder };
          newOrderQuestions[q2OrigIndex] = { ...newOrderQuestions[q2OrigIndex], dbOrder: tempDbOrder };
          itemMoved = true;
          updatePathwayConfig({ orderQuestions: newOrderQuestions });
        }
      }
    }

    if (itemMoved) {
      setHasUnsavedChanges(true);
    }
  };

  // Helper to generate a unique ID for temp questions
  const generateId = () => `id_${Math.random().toString(36).substr(2, 9)}`;
  
  // --- NEW: Eligible Parent Questions ---
  const getEligibleParentQuestions = () => {
    const allQuestions = getAllQuestions();
    // A question is eligible if it's not qualifying, not already dependent,
    // and it's not the question currently being edited/created.
    return allQuestions.filter(q => {
      const isCurrentQuestion = editingIndex !== null && 
                                ((q.isQualifying && pathwayConfig.qualifyingQuestions[editingIndex]?.id === q.id) ||
                                 (!q.isQualifying && pathwayConfig.orderQuestions[editingIndex]?.id === q.id));
      return !q.isQualifying && 
             !q.parentFieldId && 
             // (q.type === 'radio' || q.type === 'checkbox') && // REMOVED: Allow any question type as parent
             !isCurrentQuestion;
    });
  };
  // --- END NEW Eligible Parent Questions ---

  // Handle selecting an action type from the modal
  const handleActionTypeSelect = (actionType) => {
      const update = {
          toolConnection: {
              ...(pathwayConfig.toolConnection || {}),
              actionType: actionType
          }
      };
      updatePathwayConfig(update);
    // Maybe clear specific fields from other types here if needed
  };
  
  return (
    <div className="space-y-6 py-3 sm:py-4 max-w-5xl mx-auto"> {/* Added max-w-5xl and mx-auto */}
      {/* Add Question Section */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <h3 className="text-sm sm:text-base font-medium text-gray-800">Add Question</h3>
            {/* --- NEW: Expand Button --- */}
            {onExpandClick && (
              <Button 
                variant="ghost"
                size="sm"
                onClick={onExpandClick}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center p-1 h-auto -mr-1"
              >
                <Maximize2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                Expand
              </Button>
            )}
            {/* --- END NEW Expand Button --- */}
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
            Create questions for your customers to answer during the order process
          </p>
        </div>
        
        <div className="space-y-2">
          <div className={cn(
            "flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 relative",
            !canAddQuestions && "opacity-60"
          )}>
            {/* Question Input */}
            <div className="flex-1 relative">
              <Input
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Add a question..."
                className="w-full h-9 sm:h-10 text-sm border-gray-200"
                disabled={!canAddQuestions}
              />
              {!canAddQuestions && pathwayConfig.connectionEnabled && (
                <Lock className="h-4 w-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
              )}
            </div>
            
            {/* Type dropdown - Updated to use Select component */}
            <Select
              value={questionType}
              onValueChange={setQuestionType}
              disabled={!canAddQuestions}
            >
              <SelectTrigger className="w-full sm:w-40 h-9 sm:h-10 text-xs sm:text-sm border-gray-200">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              {/* Apply z-index here */}
              <SelectContent className="z-[10001]">
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="numeric">Numbers</SelectItem>
                <SelectItem value="radio">Multiple Choice</SelectItem>
                <SelectItem value="checkbox">Yes/No</SelectItem>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="time">Time</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Add button - Updated to match ComplexAnswerSettingsModal */}
            <Button 
               type="button"
              onClick={saveQuestion} 
              disabled={!canAddQuestions || !questionText.trim()}
              className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3"
              size={isMobile ? "sm" : "default"}
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
              {editingIndex !== null ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
          
        {/* Options for select/radio types */}
        {(questionType === 'radio') && (
          <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="text-xs font-medium text-gray-700 mb-2 flex items-center">
              <span>Add Options</span>
              {options.length > 0 && (
                <span className="ml-2 bg-gray-200 text-gray-700 text-[10px] px-1.5 py-0.5 rounded">
                  {options.length}
                </span>
              )}
            </div>
            
            <div className="flex space-x-2 mb-3">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Add an option" 
                className="flex-1 h-8 text-sm border-gray-200"
                onKeyPress={e => e.key === 'Enter' && addOption()}
                disabled={!canAddQuestions} // Disable if cannot add questions
              />
              <Button 
                onClick={addOption} 
                variant="outline"
                size="sm"
                disabled={!newOption.trim() || !canAddQuestions} // Disable if cannot add questions
              >
                Add
              </Button>
            </div>
              
            <div className="bg-white border border-gray-200 rounded-md p-2 max-h-[120px] overflow-y-auto">
              {options.length > 0 ? (
                <div className="space-y-1">
                  {options.map((opt, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-between items-center py-1.5 px-2 hover:bg-gray-50 rounded"
                    >
                      <span className="text-xs">{opt}</span>
                      <button 
                        onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        disabled={!canAddQuestions} // Disable if cannot add questions
                      >
                        <Trash2 size={14} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-400 text-xs py-2 text-center">No options added yet</div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Qualifying Question Toggle - Styled like Active Status */}
      <div className={cn(
        "flex items-center justify-between pt-3 sm:pt-4",
        !canAddQuestions && "opacity-60"
      )}>
        <div className="space-y-0.5 sm:space-y-1">
          <h4 className="text-xs sm:text-sm font-medium text-gray-700">Qualifying Question</h4>
          <p className="text-[10px] sm:text-xs text-gray-500">
            Mark this question as qualifying (will be asked before order process)
          </p>
        </div>
        <div className="flex items-center space-x-1">
          <Switch
            id="isQualifying"
            name="isQualifying"
            checked={isQualifying}
            onCheckedChange={(checked) => {
              setIsQualifying(checked);
              if (checked) setIsDependentQuestion(false); // Cannot be both
            }}
            className="data-[state=checked]:bg-blue-500"
            disabled={!canAddQuestions || isDependentQuestion} // Disable if dependent
          />
          {!canAddQuestions && pathwayConfig.connectionEnabled && (
            <Lock size={14} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* --- NEW: Conditional Question Toggle & Settings --- */}
      <div className={cn(
        "flex items-center justify-between pt-3 sm:pt-4",
        !canAddQuestions && "opacity-60"
      )}>
        <div className="space-y-0.5 sm:space-y-1">
          <h4 className="text-xs sm:text-sm font-medium text-gray-700">Conditional Question</h4>
          <p className="text-[10px] sm:text-xs text-gray-500">
            Show this question only if another question is answered a certain way
          </p>
        </div>
        <div className="flex items-center space-x-1">
          <Switch
            id="isDependentQuestion"
            name="isDependentQuestion"
            checked={isDependentQuestion}
            onCheckedChange={(checked) => {
              setIsDependentQuestion(checked);
              if (checked) setIsQualifying(false); // Cannot be both
              if (!checked) { // Reset dependent fields if turning off
                setParentQuestionId('');
                // setDependencyCondition('equals'); // Old state, removed
                // setDependencyValue(''); // Old state, removed
              }
            }}
            className="data-[state=checked]:bg-purple-500" // Different color
            disabled={!canAddQuestions || isQualifying} // Disable if qualifying
          />
          {!canAddQuestions && pathwayConfig.connectionEnabled && (
            <Lock size={14} className="text-gray-400" />
          )}
        </div>
      </div>

      {isDependentQuestion && canAddQuestions && (
        <motion.div 
          initial={{ opacity: 0, y: -10, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -10, height: 0 }}
          className="mt-3 space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50/50"
        >
          <div>
            <Label htmlFor="parentQuestion" className="text-xs font-medium text-gray-700 mb-1 block">
              Show when question:
            </Label>
            <Select
              value={parentQuestionId}
              onValueChange={setParentQuestionId} // useEffect will handle dependent state changes
            >
              <SelectTrigger id="parentQuestion" className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                <SelectValue placeholder="Select parent question" />
              </SelectTrigger>
              <SelectContent className="z-[10002]">
                {getEligibleParentQuestions().length === 0 && (
                  <SelectItem value="" disabled className="text-xs text-gray-400">No eligible parent questions</SelectItem>
                )}
                {getEligibleParentQuestions().map(q => (
                  <SelectItem key={q.id} value={q.id} className="text-sm">
                    {q.text} ({q.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Render new UI only if a parent question is selected and its details are loaded */}
          {parentQuestionId && selectedParentQuestionDetails && (
            <>
              {/* Main Trigger Type Selection */}
              <div className="mt-2 space-y-1">
                <Label className="text-xs font-medium text-gray-700 mb-1 block">This question should appear when "<span className='italic'>{selectedParentQuestionDetails.text}</span>"...</Label>
                <Select 
                  value={parentQuestionTriggerRule.type}
                  onValueChange={(type) => {
                    // Reset value when type changes to ensure consistency
                    let defaultValue;
                    if (type === 'matches_specific_value') {
                        // Set context-aware default for value when switching to 'matches_specific_value'
                        if (selectedParentQuestionDetails.type === 'radio' || selectedParentQuestionDetails.type === 'select') {
                            defaultValue = []; // Default to empty array for multi-select options
                        } else if (selectedParentQuestionDetails.type === 'checkbox') {
                            defaultValue = 'yes'; // Default for boolean
                        } else {
                            defaultValue = ''; // Default to empty string for text/numeric etc.
                        }
                    } else {
                        defaultValue = undefined; // No value for answered/not_answered
                    }
                    setParentQuestionTriggerRule({ type: type, value: defaultValue });
                  }}
                >
                  <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10002]">
                    <SelectItem value="answered">Is Answered</SelectItem>
                    <SelectItem value="not_answered">Is Not Answered</SelectItem>
                    <SelectItem value="matches_specific_value">Matches Specific Value(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Context-Specific Value Input */}
              {parentQuestionTriggerRule.type === 'matches_specific_value' && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  <Label className="text-xs font-medium text-gray-700 mb-0.5 block">
                    Specify the value(s) it should match:
                  </Label>
                  
                  {/* Parent Type: radio or select (Multiple Choice / Dropdown) */}
                  {(selectedParentQuestionDetails.type === 'radio' || selectedParentQuestionDetails.type === 'select') && (
                    <div className="space-y-1.5 rounded-md border border-gray-200 bg-white p-2.5 max-h-32 overflow-y-auto">
                      {selectedParentQuestionDetails.options && selectedParentQuestionDetails.options.length > 0 ? (
                        selectedParentQuestionDetails.options.map((opt, optIdx) => (
                          <div key={optIdx} className="flex items-center">
                            <input 
                              type="checkbox" 
                              id={`parent_opt_${optIdx}_${selectedParentQuestionDetails.id}`}
                              value={opt}
                              checked={(parentQuestionTriggerRule.value || []).includes(opt)}
                              onChange={(e) => {
                                const currentValues = Array.isArray(parentQuestionTriggerRule.value) ? parentQuestionTriggerRule.value : [];
                                const newValue = e.target.checked 
                                  ? [...currentValues, opt] 
                                  : currentValues.filter(v => v !== opt);
                                setParentQuestionTriggerRule({ ...parentQuestionTriggerRule, value: newValue });
                              }}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2 shadow-sm"
                            />
                            <label htmlFor={`parent_opt_${optIdx}_${selectedParentQuestionDetails.id}`} className="text-xs text-gray-700 select-none">
                              {opt}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 italic p-1">Parent question has no options defined.</p>
                      )}
                    </div>
                  )}

                  {/* Parent Type: checkbox (Yes/No) */}
                  {selectedParentQuestionDetails.type === 'checkbox' && (
                     <Select 
                        value={parentQuestionTriggerRule.value || 'yes'} // Default to 'yes' if undefined for checkbox
                        onValueChange={(val) => setParentQuestionTriggerRule({...parentQuestionTriggerRule, value: val})}
                      >
                        <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[10002]">
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                  )}

                  {/* Parent Type: text, numeric, date, time, textarea */}
                  {['text', 'numeric', 'date', 'time', 'textarea'].includes(selectedParentQuestionDetails.type) && (
                    <Input
                      type={selectedParentQuestionDetails.type === 'numeric' ? 'number' : selectedParentQuestionDetails.type === 'date' ? 'date' : selectedParentQuestionDetails.type === 'time' ? 'time' : 'text'}
                      value={parentQuestionTriggerRule.value || ''} 
                      onChange={(e) => setParentQuestionTriggerRule({...parentQuestionTriggerRule, value: e.target.value})}
                      placeholder={`Enter expected ${selectedParentQuestionDetails.type} value`}
                      className="h-9 sm:h-10 text-sm border-gray-200 bg-white"
                    />
                  )}
                  {(parentQuestionTriggerRule.value === undefined || (Array.isArray(parentQuestionTriggerRule.value) && parentQuestionTriggerRule.value.length === 0)) && 
                    (selectedParentQuestionDetails.type === 'radio' || selectedParentQuestionDetails.type === 'select') && (
                    <p className="text-[10px] text-orange-600 mt-1">Please select at least one option for the condition to be valid.</p>
                  )}
                   {(parentQuestionTriggerRule.value === undefined || parentQuestionTriggerRule.value === '') && 
                    !Array.isArray(parentQuestionTriggerRule.value) && (selectedParentQuestionDetails.type !== 'radio' && selectedParentQuestionDetails.type !== 'select' && selectedParentQuestionDetails.type !== 'checkbox') && (
                    <p className="text-[10px] text-orange-600 mt-1">Please enter a value for the condition to be valid.</p>
                  )}
                </div>
              )}
            </>
          )}
        </motion.div>
      )}
      {/* --- END NEW Conditional Question --- */}
      
      {/* Divider after qualifying toggle */}
      <div className="border-t border-gray-100 mt-3"></div>
      
      {/* All Questions Section - Combined qualifying and order questions */}
      <div className="mt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center mb-1 sm:mb-2">
              <HelpCircle size={isMobile ? 14 : 16} className="text-gray-600 mr-1.5 sm:mr-2" />
              <span className="text-xs text-gray-600 font-medium">Order Questions</span>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              Questions will be asked in the order shown below
            </p>
          </div>
          
          <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
            {(pathwayConfig.qualifyingQuestions?.length || 0) + (pathwayConfig.orderQuestions?.length || 0)} 
            {((pathwayConfig.qualifyingQuestions?.length || 0) + (pathwayConfig.orderQuestions?.length || 0)) === 1 ? ' question' : ' questions'}
          </div>
        </div>
        
        {/* Display all questions */}
        <div className="min-h-[200px] bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 relative mt-2">
          {(pathwayConfig.qualifyingQuestions?.length || 0) === 0 && (pathwayConfig.orderQuestions?.length || 0) === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-6 sm:py-8">
              <HelpCircle className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mb-1.5 sm:mb-2" />
              <p className="text-xs sm:text-sm text-gray-400">No questions added yet</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1">Add questions above to collect information during the order process</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* MODIFIED: Use getHierarchicalQuestionsForDisplay for rendering */}
              {getHierarchicalQuestionsForDisplay().map((question, renderIdx) => ( // renderIdx is now from the flattened list
                <motion.div
                  key={`question-${question.id}`} // Use unique question ID
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                  style={{ marginLeft: `${question.depth * 20}px` }} // Indentation based on depth
                >
                  <div className="flex items-center border-b border-gray-100 px-3 py-2">
                    <div className="flex-shrink-0 mr-2 flex items-center">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full w-5 h-5 flex items-center justify-center">
                        {question.renderIndex + 1} {/* Use renderIndex for display numbering */}
                      </span>
                    </div>
                    
                    <div className="flex-1 flex items-center">
                      <div className={`text-xs px-2 py-0.5 rounded ${ // Check for null/undefined question.type
                        question.type === 'checkbox' ? 'bg-green-100 text-green-800' :
                        question.type === 'date' || question.type === 'time' ? 'bg-blue-100 text-blue-800' :
                        question.type === 'select' || question.type === 'radio' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {question.type === 'checkbox' ? 'Yes/No' :
                         question.type === 'select' ? 'Dropdown' :
                         question.type === 'radio' ? 'Multiple Choice' :
                         question.type === 'textarea' ? 'Long Text' :
                         question.type === 'numeric' ? 'Numbers' :
                         (question.type?.charAt(0).toUpperCase() + question.type?.slice(1)) || 'Text'} { /* Handle potential undefined type */}
                      </div>
                      
                      {question.isQualifying && (
                        <div className="ml-2">
                          <span className="bg-blue-50 text-blue-600 text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded border border-blue-200">
                            Qualifying
                          </span>
                        </div>
                      )}
                      {/* --- NEW: Display if Conditional --- */}
                      {question.parentFieldId && (
                        <div className="ml-2">
                          <span className="bg-purple-50 text-purple-600 text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded border border-purple-200">
                            Conditional
                          </span>
                        </div>
                      )}
                      {/* --- END NEW Display --- */}
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      {(!pathwayConfig.toolConnection?.actionId || question.isEditable) ? (
                        <>
                           {/* Move Up */}
                           {/* MODIFIED: Pass only question and direction to moveQuestion */}
                           {question.renderIndex > 0 && ( 
                            <button type="button" onClick={() => moveQuestion(question, 'up')} className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Move up">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="m18 15-6-6-6 6"/></svg>
                            </button>
                          )}
                          {/* Move Down */}
                           {/* MODIFIED: Pass only question and direction to moveQuestion */}
                          {question.renderIndex < getHierarchicalQuestionsForDisplay().length - 1 && ( 
                            <button type="button" onClick={() => moveQuestion(question, 'down')} className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Move down">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="m6 9 6 6 6-6"/></svg>
                            </button>
                          )}
                          {/* Edit */}
                          {/* MODIFIED: Pass full question object from display list */}
                          <button type="button" onClick={() => editQuestion(question)} className="p-1 text-gray-400 hover:text-blue-500 focus:outline-none" aria-label="Edit question">
                            <Settings className="h-3.5 w-3.5" />
                          </button>
                          {/* Delete */}
                          {/* MODIFIED: Pass full question object */}
                          <button type="button" onClick={() => deleteQuestion(question)} className="p-1 text-gray-400 hover:text-red-500 focus:outline-none" aria-label="Delete question">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          {pathwayConfig.connectionEnabled && <Unlock size={14} className="text-gray-400" />}
                        </>
                      ) : (
                          pathwayConfig.connectionEnabled && <Lock size={14} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  <div className="px-3 py-2.5">
                    <p className="text-sm text-gray-800">{question.text}</p>
                    {(question.type === 'select' || question.type === 'radio') && question.options?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {question.options.map((opt, optIdx) => (
                          <span key={optIdx} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {opt}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* --- NEW: Display parent question info --- */}
                    {question.parentFieldId && (
                      <div className="mt-1.5 text-[10px] text-gray-500 border-t border-gray-100 pt-1.5">
                        <span className="font-medium">Depends on:</span> {
                          getAllQuestions().find(q => q.id === question.parentFieldId)?.text || 'Unknown Question'
                        }
                        {question.criteria && question.criteria.triggers && question.criteria.triggers.length > 0 && (
                          <span className="ml-1 text-purple-600">
                            (when "{getAllQuestions().find(q => q.id === question.parentFieldId)?.text || '-'}" {question.criteria.triggers[0].condition}
                            {question.criteria.triggers[0].value !== undefined 
                              ? Array.isArray(question.criteria.triggers[0].value) 
                                ? ` [${question.criteria.triggers[0].value.join(", ")}]`
                                : ` '${question.criteria.triggers[0].value}'`
                              : ''})
                          </span>
                        )}
                      </div>
                    )}
                    {/* --- END NEW Display parent question info --- */}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* --- NEW: Action Selection Section --- */}
      <div className="pt-6 border-t border-gray-100">
        <h3 className="text-sm sm:text-base font-medium text-gray-800 mb-2">Primary Action</h3>
        <p className="text-[10px] sm:text-xs text-gray-500 mb-3">
          Select the primary action this pathway will perform after collecting information.
        </p>

        {pathwayConfig.toolConnection?.actionType ? (
          <div 
            className="flex items-center p-3 border rounded-lg border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
            onClick={() => setIsActionSelectorModalOpen(true)}
          >
            <div className="flex-1 flex items-center">
              {pathwayConfig.toolConnection.actionType === 'sms' && (
                <><MessageCircle className="h-4 w-4 text-blue-500 mr-2" /> <span className="font-medium text-sm text-gray-900">Text Message</span></>
              )}
              {pathwayConfig.toolConnection.actionType === 'email' && (
                <><Mail className="h-4 w-4 text-indigo-500 mr-2" /> <span className="font-medium text-sm text-gray-900">Email</span></>
              )}
              {pathwayConfig.toolConnection.actionType === 'transfer' && (
                <><PhoneForwarded className="h-4 w-4 text-green-500 mr-2" /> <span className="font-medium text-sm text-gray-900">Transfer Call</span></>
              )}
              {pathwayConfig.toolConnection.actionType === 'api' && (
                <><Webhook className="h-4 w-4 text-purple-500 mr-2" /> <span className="font-medium text-sm text-gray-900">API</span></>
              )}
               {!pathwayConfig.toolConnection.actionType && (
                 <span className="font-medium text-sm text-gray-500">No Action Selected</span>
               )}
            </div>
            <span className="text-xs text-blue-600 font-medium">Change</span>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsActionSelectorModalOpen(true)}
            className="w-full border-dashed"
          >
            <Plus className="h-4 w-4 mr-2" />
            Select Action Type
          </Button>
        )}
      </div>
      {/* --- End Action Selection --- */}
      
      {/* Tips Section */}
      <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-3 sm:p-4">
        <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
          <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
          Tips for creating effective questions
        </h4>
        <ul className="mt-1.5 sm:mt-2 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-gray-600">
          <li className="flex items-start">
            <div className="rounded-full h-1 w-1 bg-blue-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
            <span>Keep questions clear and concise for better customer experience</span>
          </li>
          <li className="flex items-start">
            <div className="rounded-full h-1 w-1 bg-blue-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
            <span>Use qualifying questions to pre-screen customers before order process</span>
          </li>
          <li className="flex items-start">
            <div className="rounded-full h-1 w-1 bg-blue-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
            <span>Use dropdown options when there are multiple predefined choices</span>
          </li>
        </ul>
      </div>

      {/* Action Selector Modal */}
      <SimpleActionToolModal
        isOpen={isActionSelectorModalOpen}
        onClose={() => setIsActionSelectorModalOpen(false)}
        onToolSelect={handleActionTypeSelect}
      />
    </div>
  );
};

// Move ConnectionTab outside the main component
const ConnectionTab = ({ pathwayConfig, setPathwayConfig }) => {
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [selectedParam, setSelectedParam] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Check if we're using a predefined connection
  const usingPredefinedConnection = pathwayConfig.toolConnection?.connectionId;
  
  // Sample parameters for each endpoint type
  const endpointParams = {
    curbside: ['accountId', 'pickup_date', 'pickup_time', 'delivery_date', 'delivery_time', 'services', 'instructions', 'routeId', 'curbsideAccountPhone'],
    cleancloud: ['customer_id', 'service_type', 'pickup_datetime', 'delivery_datetime', 'garment_details', 'special_instructions'],
    custom: ['param1', 'param2', 'param3', 'param4']
  };
  
  // Sample preset connections for display purposes
  const savedConnections = [
    { id: 'conn1', name: 'Main Curbside Account', type: 'curbside', accountId: 'acc123' },
    { id: 'conn2', name: 'CleanCloud Test', type: 'cleancloud', storeId: 'store456' },
  ];

  // Sample preset actions for each connection type
  const connectionActions = {
    curbside: [
      { id: 'place-order', name: 'Place New Order', endpoint: '/api/curbside/order' },
      { id: 'cancel-order', name: 'Cancel Order', endpoint: '/api/curbside/cancel' },
    ],
    cleancloud: [
      { id: 'place-order', name: 'Create Order', endpoint: '/api/cleancloud/orders' },
      { id: 'pickup-request', name: 'Schedule Pickup', endpoint: '/api/cleancloud/pickup' },
    ],
    custom: [
      { id: 'custom-action', name: 'Custom API Action', endpoint: '' }
    ]
  };
  
  const addMapping = () => {
    if (selectedQuestion && selectedParam) {
      const newParams = [...pathwayConfig.toolConnection.parameters];
      // Check if parameter already exists
      const existingIndex = newParams.findIndex(p => p.param === selectedParam);
      
      if (existingIndex >= 0) {
        newParams[existingIndex] = {
          param: selectedParam,
          questionId: selectedQuestion
        };
      } else {
        newParams.push({
          param: selectedParam,
          questionId: selectedQuestion
        });
      }
      
      setPathwayConfig({
        ...pathwayConfig,
        toolConnection: {
          ...pathwayConfig.toolConnection,
          parameters: newParams
        }
      });
      
      setSelectedQuestion('');
      setSelectedParam('');
    }
  };
  
  const removeMapping = (paramName) => {
    const newParams = pathwayConfig.toolConnection.parameters.filter(
      p => p.param !== paramName
    );
    
    setPathwayConfig({
      ...pathwayConfig,
      toolConnection: {
        ...pathwayConfig.toolConnection,
        parameters: newParams
      }
    });
  };
  
  // Get the current selected connection
  const selectedConnection = usingPredefinedConnection
    ? savedConnections.find(c => c.id === pathwayConfig.toolConnection.connectionId)
    : null;
    
  // Get the current selected action
  const selectedAction = usingPredefinedConnection && pathwayConfig.toolConnection.actionId
    ? connectionActions[pathwayConfig.toolConnection.endpointType]?.find(
        a => a.id === pathwayConfig.toolConnection.actionId
      )
    : null;
  
  // Render the predefined connection view
  const renderPredefinedConnection = () => {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <div className="flex items-start">
            <div className="mr-3 mt-0.5">
              <Layers size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="font-medium mb-1">Using App Connection</p>
              <p>
                This order pathway is using a predefined app connection. Parameter mapping is handled automatically.
                Toggle advanced options to customize HTTP headers and request format.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border shadow-sm">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-medium">Connection Details</h3>
            <button 
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
            </button>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Connection Type</div>
                <div className="font-medium capitalize">{selectedConnection?.name || pathwayConfig.toolConnection.endpointType}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Action</div>
                <div className="font-medium">
                  {selectedAction?.name || 'Custom Action'}
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="text-sm text-gray-500 mb-1">Endpoint</div>
              <div className="font-medium text-gray-800 bg-gray-50 p-2 rounded border font-mono text-sm">
                {pathwayConfig.toolConnection.endpoint || '/api/place-order'}
              </div>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 pt-3 border-t mt-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Authentication</label>
                  <select className="border rounded w-full p-2 text-sm">
                    <option>OAuth 2.0 (Default)</option>
                    <option>API Key</option>
                    <option>Basic Auth</option>
                    <option>None</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">HTTP Headers</label>
                  <div className="border rounded">
                    <div className="p-3 border-b">
                      <div className="grid grid-cols-5 gap-2">
                        <div className="col-span-2">
                          <input 
                            type="text" 
                            className="border rounded w-full p-1.5 text-sm"
                            placeholder="Header name"
                            value="Content-Type"
                            readOnly
                          />
                        </div>
                        <div className="col-span-2">
                          <input 
                            type="text" 
                            className="border rounded w-full p-1.5 text-sm"
                            placeholder="Value"
                            value="application/json"
                            readOnly
                          />
                        </div>
                        <div className="flex items-center justify-center">
                          <button className="text-gray-400 cursor-not-allowed">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 border-b">
                      <div className="grid grid-cols-5 gap-2">
                        <div className="col-span-2">
                          <input 
                            type="text" 
                            className="border rounded w-full p-1.5 text-sm"
                            placeholder="Header name"
                            value="Authorization"
                            readOnly
                          />
                        </div>
                        <div className="col-span-2">
                          <input 
                            type="text" 
                            className="border rounded w-full p-1.5 text-sm"
                            placeholder="Value"
                            value="Bearer [token]"
                            readOnly
                          />
                        </div>
                        <div className="flex items-center justify-center">
                          <button className="text-gray-400 cursor-not-allowed">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3">
                      <div className="grid grid-cols-5 gap-2">
                        <div className="col-span-2">
                          <input 
                            type="text" 
                            className="border rounded w-full p-1.5 text-sm"
                            placeholder="Header name"
                          />
                        </div>
                        <div className="col-span-2">
                          <input 
                            type="text" 
                            className="border rounded w-full p-1.5 text-sm"
                            placeholder="Value"
                          />
                        </div>
                        <div className="flex items-center justify-center">
                          <button className="text-blue-600">
                            <PlusCircle size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Request Format</label>
                  <div className="relative">
                    <pre className="bg-gray-800 text-gray-100 rounded-lg p-3 text-sm font-mono overflow-auto h-48">
{`{
  // Auto-generated from parameter mapping
  "accountId": "{{accountId}}",
  "pickup_date": "{{pickup_date}}",
  "pickup_time": "{{pickup_time}}",
  "delivery_date": "{{delivery_date}}",
  "delivery_time": "{{delivery_time}}",
  "services": ["{{services}}"],
  "instructions": "{{instructions}}",
  "curbsideAccountPhone": "{{curbsideAccountPhone}}"
}`}
                    </pre>
                    <div className="absolute top-3 right-3">
                      <button className="bg-gray-700 text-gray-300 p-1.5 rounded hover:bg-gray-600">
                        <Settings size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Template variables wrapped in {'{{'} braces {'}}'}  will be replaced with actual values from mapped questions.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="rounded-lg border shadow-sm">
          <div className="p-4 border-b">
            <h3 className="font-medium">Parameter Mapping</h3>
          </div>
          
          <div className="divide-y">
            {pathwayConfig.toolConnection.parameters.map((mapping, idx) => {
              // Find the question text
              const question = [...(pathwayConfig.qualifyingQuestions || []), ...(pathwayConfig.orderQuestions || [])].find(q => q.id === mapping.questionId);
              const questionText = question ? question.text : (mapping.questionId === 'fixed' ? `Fixed: ${mapping.value}` : 'Unknown Question');
              
                return (
                  <div key={idx} className="p-4 flex justify-between items-center">
                    <div>
                      <div className="font-medium">{mapping.param}</div>
                      <div className="text-sm text-gray-500">
                      {mapping.questionId === 'fixed' ? 'Fixed value' : 'Mapped to:'} {questionText}
                    </div>
                  </div>
                  <div className="text-gray-400">(Auto-configured)</div>
                </div>
              );
            })}
            {pathwayConfig.toolConnection.parameters.length === 0 && (
              <div className="p-4 text-center text-gray-500">
                No parameter mappings configured yet
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };
  
  // Render the custom connection view
  const renderCustomConnection = () => {
  return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-4"
      >
      <div>
        <label className="block text-sm font-medium mb-1">Endpoint Type</label>
        <select 
          className="border rounded w-full p-2"
          value={pathwayConfig.toolConnection.endpointType}
          onChange={e => setPathwayConfig({
            ...pathwayConfig,
            toolConnection: {
              ...pathwayConfig.toolConnection,
              endpointType: e.target.value,
              parameters: [] // Reset parameters when endpoint type changes
            }
          })}
        >
          <option value="curbside">Curbside</option>
          <option value="cleancloud">CleanCloud</option>
          <option value="custom">Custom Endpoint</option>
        </select>
      </div>
      
      {pathwayConfig.toolConnection.endpointType === 'custom' && (
        <div>
          <label className="block text-sm font-medium mb-1">Custom Endpoint URL</label>
          <input 
            type="text" 
            className="border rounded w-full p-2"
            value={pathwayConfig.toolConnection.endpoint}
            onChange={e => setPathwayConfig({
              ...pathwayConfig,
              toolConnection: {
                ...pathwayConfig.toolConnection,
                endpoint: e.target.value
              }
            })}
              placeholder="https://api.example.com" 
          />
        </div>
      )}
      
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-md font-medium mb-3">Parameter Mapping</h3>
        <p className="text-sm text-gray-600 mb-3">
          Map your questions to the endpoint parameters
        </p>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mb-4">
          <div className="flex-1">
            <label className="block text-sm mb-1">Question</label>
            <select 
              className="border rounded w-full p-2"
              value={selectedQuestion}
              onChange={e => setSelectedQuestion(e.target.value)}
            >
              <option value="">Select a question</option>
              {/* Combine qualifying and order questions */} 
              {[...(pathwayConfig.qualifyingQuestions || []), ...(pathwayConfig.orderQuestions || [])].map((q, idx) => (
                <option key={q.id || `q-${idx}`} value={q.id}> 
                  {q.text}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm mb-1">Parameter</label>
            <select 
              className="border rounded w-full p-2"
              value={selectedParam}
              onChange={e => setSelectedParam(e.target.value)}
            >
              <option value="">Select a parameter</option>
              {(endpointParams[pathwayConfig.toolConnection.endpointType] || []).map((param, idx) => (
                <option key={idx} value={param}>
                  {param}
                </option>
              ))}
            </select>
          </div>
          
          <div className="sm:flex sm:items-end">
            <button 
              onClick={addMapping} 
              className="bg-blue-600 text-white rounded px-4 py-2 w-full sm:w-auto"
              disabled={!selectedQuestion || !selectedParam}
            >
              Map
            </button>
          </div>
        </div>
        
        <div className="bg-white border rounded">
          {pathwayConfig.toolConnection.parameters.length > 0 ? (
            pathwayConfig.toolConnection.parameters.map((mapping, idx) => {
              // Find the question text
              const question = [...(pathwayConfig.qualifyingQuestions || []), ...(pathwayConfig.orderQuestions || [])].find(q => q.id === mapping.questionId);
              const questionText = question ? question.text : 'Unknown Question';
              
              return (
                <div key={idx} className="border-b last:border-b-0 p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{mapping.param}</div>
                    <div className="text-sm text-gray-500">
                      Mapped to: {questionText}
                    </div>
                  </div>
                  <button onClick={() => removeMapping(mapping.param)} className="text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })
          ) : (
            <div className="p-4 text-center text-gray-500">
              No parameter mappings added yet
            </div>
          )}
        </div>
      </div>
      </motion.div>
  );
  };
  
  // Conditionally render based on connection type
  return usingPredefinedConnection ? renderPredefinedConnection() : renderCustomConnection();
};

// Move TriggersTab outside the main component
const TriggersTab = ({ 
  pathwayConfig, 
  setPathwayConfig, 
  showLocationVariants, // Added
  handleFormChangeWithVariants, // Added
  setHasUnsavedChanges // Added
}) => {
  const [currentTrigger, setCurrentTrigger] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateTriggers = (newTriggers) => {
      if (showLocationVariants) {
          handleFormChangeWithVariants('triggers', newTriggers);
      } else {
          setPathwayConfig(prev => ({ ...prev, triggers: newTriggers }));
      }
      setHasUnsavedChanges(true);
  };

  const addTrigger = (e) => {
    e.preventDefault();
    if (!currentTrigger.trim()) return;
    const newTrigger = currentTrigger.trim().toLowerCase();
    const currentTriggers = pathwayConfig.triggers || [];
    if (currentTriggers.includes(newTrigger)) {
      setCurrentTrigger('');
      return;
    }
    updateTriggers([...currentTriggers, newTrigger]);
    setCurrentTrigger('');
  };

  const removeTrigger = (trg) => {
    const currentTriggers = pathwayConfig.triggers || [];
    updateTriggers(currentTriggers.filter(t => t !== trg));
  };

  return (
    <div className="space-y-6 py-3 sm:py-4">
      <div className="space-y-1.5 sm:space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center mb-1 sm:mb-2">
              <MessageSquare size={isMobile ? 14 : 16} className="text-gray-600 mr-1.5 sm:mr-2" />
              <span className="text-xs text-gray-600 font-medium">Trigger Keywords</span>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              Add words or phrases that will trigger this pathway
            </p>
          </div>
          {pathwayConfig.triggers?.length > 0 && (
            <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
              {pathwayConfig.triggers.length} {pathwayConfig.triggers.length === 1 ? 'keyword' : 'keywords'}
            </div>
          )}
        </div>

        <div className="min-h-[120px] sm:min-h-[150px] bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 relative">
          {(!pathwayConfig.triggers || pathwayConfig.triggers.length === 0) ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-6 sm:py-8">
              <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mb-1.5 sm:mb-2" />
              <p className="text-xs sm:text-sm text-gray-400">No keywords added yet</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {pathwayConfig.triggers.map((t, idx) => (
                <div key={idx} className="bg-white border border-gray-200 text-gray-800 text-[10px] sm:text-xs px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-md flex items-center shadow-sm">
                  <span>{t}</span>
                  <button onClick={() => removeTrigger(t)} className="ml-1 sm:ml-1.5 text-gray-400 hover:text-gray-600 p-0.5">
                    <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={addTrigger} className="mt-3 flex space-x-2">
          <Input
            value={currentTrigger}
            onChange={(e) => setCurrentTrigger(e.target.value)}
            placeholder="Type a keyword and press Enter"
            className="flex-1 h-9 sm:h-10 text-sm border-gray-200"
          />
          <Button type="submit" disabled={!currentTrigger.trim()} className="px-3 text-xs sm:text-sm">
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5" />Add
          </Button>
        </form>
      </div>
    </div>
  );
};

// Move LocationsTab outside the main component
const LocationsTab = ({ 
  pathwayConfig, 
  setPathwayConfig,
  showLocationVariants, // Added
  handleFormChangeWithVariants, // Added
  setHasUnsavedChanges // Added
}) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const supabase = getSupabaseClient();

  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLocations([]); return; }
        const { data, error } = await supabase
          .from('vapi_location_settings')
          .select('id,name,google_address')
          .eq('account_id', user.id)
          .order('name');
        if (error) throw error;
        setLocations(data || []);
      } catch (err) {
        console.error('Error fetching locations', err);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const updateLocationSettings = (update) => {
     if (showLocationVariants) {
        // Location assignments are handled by the side panel and its callback
        // This tab might become read-only or display differently in variant mode
        // For now, let's just log that the variant handler should be used.
        // console.warn("LocationsTab direct update called in variant mode. This should be handled by side panel.");
        // If we *need* to update from here, call the variant handler:
        // Object.keys(update).forEach(key => {
        //    handleFormChangeWithVariants(key, update[key]);
        // });
     } else {
         setPathwayConfig(prev => ({ ...prev, ...update }));
     }
     setHasUnsavedChanges(true);
  };

  const toggleAll = () => {
    const isCurrentlyAll = pathwayConfig.all_locations || false;
    updateLocationSettings({
      all_locations: !isCurrentlyAll,
      selected_locations: !isCurrentlyAll ? [] : (pathwayConfig.selected_locations || []) // Ensure array
    });
  };

  const toggleLocation = (locId) => {
    const selected = pathwayConfig.selected_locations || [];
    const isSelected = selected.includes(locId);
    updateLocationSettings({
      selected_locations: isSelected ? selected.filter(id=>id!==locId) : [...selected, locId],
      all_locations: false
    });
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading locations...</div>;

  if (locations.length === 0) return <div className="p-6 text-center text-gray-500">No locations available.</div>;

  // Decide how to render based on variant mode
  // Maybe disable controls or show a message if variants are on?
  const isReadOnly = showLocationVariants;

  return (
    <div className="space-y-4 py-3 sm:py-4">
      {isReadOnly && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center text-xs text-blue-700">
              Location assignments are managed in the Location Variants panel.
          </div>
      )}
      <div 
          className={cn(
              "border rounded-lg p-4 flex items-center", 
              isReadOnly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
          )} 
          onClick={!isReadOnly ? toggleAll : undefined}
      >
        <input 
            type="checkbox" 
            readOnly 
            className="mr-3" 
            checked={pathwayConfig.all_locations || false} 
            disabled={isReadOnly}
        />
        <span className="text-sm">All Locations</span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {locations.map(loc => (
          <div 
              key={loc.id} 
              className={cn(
                  "border rounded-lg p-4 flex items-center", 
                  (pathwayConfig.all_locations || isReadOnly) ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-gray-50'
              )} 
              onClick={()=> !(pathwayConfig.all_locations || isReadOnly) && toggleLocation(loc.id)}
          > 
            <input 
                type="checkbox" 
                readOnly 
                className="mr-3" 
                checked={(pathwayConfig.selected_locations||[]).includes(loc.id)} 
                disabled={pathwayConfig.all_locations || isReadOnly} 
            />
            <div>
              <div className="text-sm font-medium">{loc.name}</div>
              {loc.google_address && <div className="text-xs text-gray-500 truncate">{loc.google_address}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Builder Component - Now accepts state and setters as props
const OrderPathwayBuilder = ({ 
  pathwayConfig, 
  setPathwayConfig, 
  validationErrors, 
  isModal, 
  activeTab,
  // --- Add variant props ---
  locations,
  showLocationVariants,
  setShowLocationVariants,
  activeLocationGrouping,
  handleFormChangeWithVariants, // Pass variant handler
  setHasUnsavedChanges, // Pass unsaved changes setter
  onExpandClick // <-- Prop received from OrderPathwayModal
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to update toolConnection state
  // Decide whether to use variant handler or direct state set based on showLocationVariants
  const updateToolConnection = (name, value) => {
    // const updateFn = showLocationVariants ? handleFormChangeWithVariants : setPathwayConfig;
    // const fieldName = showLocationVariants ? `toolConnection.${name}` : 'toolConnection';
    
    if (showLocationVariants) {
        handleFormChangeWithVariants(`toolConnection.${name}`, value);
    } else {
        setPathwayConfig(prev => ({
          ...prev,
          toolConnection: {
            ...(prev.toolConnection || {}),
            [name]: value
          }
        }));
    }
    setHasUnsavedChanges(true);
  };

  // Choose the correct state update function based on variants mode
  const updateState = useCallback((name, value) => {
      if (showLocationVariants) {
          handleFormChangeWithVariants(name, value);
      } else {
          // Check if name indicates nested structure (like toolConnection)
          if (name.includes('.')) {
              const keys = name.split('.');
              setPathwayConfig(prev => {
                  let updated = { ...prev };
                  let current = updated;
                  for (let i = 0; i < keys.length - 1; i++) {
                      current[keys[i]] = { ...(current[keys[i]] || {}) };
                      current = current[keys[i]];
                  }
                  current[keys[keys.length - 1]] = value;
                  return updated;
              });
          } else {
             setPathwayConfig(prev => ({ ...prev, [name]: value }));
          }
      }
      setHasUnsavedChanges(true);
  }, [showLocationVariants, handleFormChangeWithVariants, setPathwayConfig, setHasUnsavedChanges]);

  // Prepare the questions list once for all MentionTextareas in this component scope
  const allQuestionsForMentions = React.useMemo(() => {
    return (
      pathwayConfig.qualifyingQuestions || pathwayConfig.orderQuestions
        ? [
            ...(pathwayConfig.qualifyingQuestions || []),
            ...(pathwayConfig.orderQuestions || [])
          ].map(q => ({ id: q.id, text: q.text }))
        : []
    );
  }, [pathwayConfig.qualifyingQuestions, pathwayConfig.orderQuestions]);

  // Calculate effective SMS length using useMemo for efficiency
  const smsHtmlContent = pathwayConfig.toolConnection?.smsMessage || '';
  const effectiveSmsLength = useMemo(() => calculateEffectiveSmsLength(smsHtmlContent), [smsHtmlContent]);

  return (
    <Tabs defaultValue="general" value={activeTab}>
      <div className={isModal ? "pb-4" : "p-6"}>
        <TabsContent value="general" forceMount={true} className={activeTab !== 'general' ? 'hidden' : ''}>
              <GeneralSettingsTab 
                pathwayConfig={pathwayConfig}
                setPathwayConfig={updateState} 
                validationErrors={validationErrors}
                locations={locations}
                showLocationVariants={showLocationVariants}
                setShowLocationVariants={setShowLocationVariants}
                setHasUnsavedChanges={setHasUnsavedChanges}
                questions={allQuestionsForMentions} // <<< ADDED: Pass the prepared questions list
              />
            </TabsContent>
            
        <TabsContent value="triggers" forceMount={true} className={activeTab !== 'triggers' ? 'hidden' : ''}>
                <TriggersTab 
                  pathwayConfig={pathwayConfig}
                  setPathwayConfig={setPathwayConfig} // Keep original setter for complex array updates
                  showLocationVariants={showLocationVariants} // Pass variant flag
                  handleFormChangeWithVariants={handleFormChangeWithVariants} // Pass variant handler
                  setHasUnsavedChanges={setHasUnsavedChanges}
                />
            </TabsContent>
            
        <TabsContent value="questions" forceMount={true} className={activeTab !== 'questions' ? 'hidden' : ''}>
          <QuestionsTab 
            pathwayConfig={pathwayConfig}
            setPathwayConfig={setPathwayConfig} // Keep original setter for complex array updates
            showLocationVariants={showLocationVariants} // Pass variant flag
            handleFormChangeWithVariants={handleFormChangeWithVariants} // Pass variant handler
            setHasUnsavedChanges={setHasUnsavedChanges}
            onExpandClick={onExpandClick} // --- Pass onExpandClick down to QuestionsTab --- 
          />
        </TabsContent>
        
        <TabsContent value="actions" forceMount={true} className={activeTab !== 'actions' ? 'hidden' : ''}>
          <motion.div 
            key="actions-content"
            initial={{ opacity: 0, y: 5 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.3 }}
            className="space-y-4 sm:space-y-6 py-3 sm:py-4"
          >
            {/* Show placeholder if no action type selected */}
            {!pathwayConfig?.toolConnection?.actionType && (
              <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Settings className="h-5 w-5 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">No Action Type Selected</h3>
                <p className="text-xs text-gray-500 text-center max-w-sm mb-4">
                  Please select an action type in the Questions tab to configure it.
                </p>
                {/* Optionally add a button to switch tabs here? */}
              </div>
            )}

            {/* SMS Form - Updated handlers */}
            {pathwayConfig?.toolConnection?.actionType === 'sms' && (
              <div className="space-y-4 sm:space-y-5">
                {/* Link URL - REMOVED */}
                {/* <div className="space-y-1.5 sm:space-y-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                    <Link className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
                    Action Link <span className="text-gray-400 ml-1 text-[10px] sm:text-xs font-normal">(Optional)</span>
                  </h3>
                   <p className="text-[10px] sm:text-xs text-gray-500">
                      Add a URL to include in your message
                   </p>
                  <Input
                    id="smsLinkUrl"
                    name="smsLinkUrl"
                    value={pathwayConfig.toolConnection.smsLinkUrl || ''}
                    onChange={(e) => updateToolConnection('smsLinkUrl', e.target.value)}
                    placeholder="https://example.com"
                    className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                  />
                </div> */}
                {/* Message Content */}
                {/* Apply border-top conditionally if Link URL was removed */}
                <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                    <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
                    Text Message <span className="text-red-500 ml-0.5">*</span>
                  </h3>
                   <p className="text-[10px] sm:text-xs text-gray-500">
                      This message will be sent when the action is triggered
                   </p>
                  <RichTextEditor
                    value={pathwayConfig.toolConnection.smsMessage || ''}
                    onChange={(htmlContent) => updateToolConnection('smsMessage', htmlContent)}
                    placeholder="Enter the message to send... (type '@' to mention questions)"
                    questions={allQuestionsForMentions}
                    className={cn(
                      "min-h-[100px] sm:min-h-[120px]",
                      validationErrors?.smsMessage && "!border-red-300 focus:!border-red-500 focus:!ring-red-500" // Use ! to ensure override if needed
                    )}
                  />
                   <div className="flex justify-between items-center">
                    <div className={cn("text-[10px] sm:text-xs", effectiveSmsLength > 160 ? "text-red-500" : "text-gray-500")}>
                      {effectiveSmsLength}/160 characters
                    </div>
                  </div>
                </div>
                {/* Recipient Toggle */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                     <div className="space-y-0.5 sm:space-y-1">
                         <h4 className="text-xs sm:text-sm font-medium text-gray-700">Send to specific numbers</h4>
                         <p className="text-[10px] sm:text-xs text-gray-500">
                           {pathwayConfig.toolConnection.smsUseContactPhone
                             ? "Uses pre-defined phone numbers"
                             : "Uses caller's phone number automatically"}
                         </p>
                       </div>
                     <Switch
                      id="smsUseContactPhone"
                      name="smsUseContactPhone"
                      checked={pathwayConfig.toolConnection.smsUseContactPhone || false}
                      onCheckedChange={(checked) => updateToolConnection('smsUseContactPhone', checked)}
                      className="data-[state=checked]:bg-blue-500"
                    />
                  </div>
                </div>
                {/* Phone Number Input */}
                {pathwayConfig.toolConnection.smsUseContactPhone && (
                  <div className="space-y-1.5 sm:space-y-2 pt-3">
                     <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                         Recipients <span className="text-red-500 ml-0.5">*</span>
                      </h3>
                      <p className="text-[10px] sm:text-xs text-gray-500">
                         Add phone numbers that will receive this message
                      </p>
                    <PhoneNumberInput
                      phoneNumbers={pathwayConfig.toolConnection.smsNumbers || []}
                      onChange={(numbers) => updateToolConnection('smsNumbers', numbers)}
                      error={validationErrors?.smsNumbers}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Email Form - Updated handlers */}
            {pathwayConfig?.toolConnection?.actionType === 'email' && (
              <div className="space-y-4 sm:space-y-5">
                 {/* Subject */}
                 <div className="space-y-1.5 sm:space-y-2">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                        Email Subject <span className="text-red-500 ml-0.5">*</span>
                     </h3>
                    <Input
                       id="emailSubject"
                       name="emailSubject"
                       value={pathwayConfig.toolConnection.emailSubject || ''}
                       onChange={(e) => updateToolConnection('emailSubject', e.target.value)}
                       placeholder="Enter email subject line"
                       className={cn(
                         "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                         validationErrors?.emailSubject && "border-red-300 focus:border-red-500 focus:ring-red-500"
                       )}
                    />
                 </div>
                {/* Message */}
                <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                       <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                       Email Message <span className="text-red-500 ml-0.5">*</span>
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-500">
                       Enter the email message body
                    </p>
                  <RichTextEditor
                    value={pathwayConfig.toolConnection.emailMessage || ''}
                    onChange={(htmlContent) => updateToolConnection('emailMessage', htmlContent)}
                    placeholder="Enter the email message body... (type '@' to mention questions)"
                    questions={allQuestionsForMentions}
                    className={cn(
                      "min-h-[120px] sm:min-h-[140px]",
                      validationErrors?.emailMessage && "!border-red-300 focus:!border-red-500 focus:!ring-red-500"
                    )}
                  />
                </div>
                {/* Recipient Toggle */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5 sm:space-y-1">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-700">Send to specific addresses</h4>
                        <p className="text-[10px] sm:text-xs text-gray-500">
                          {pathwayConfig.toolConnection.emailUseContactEmail
                            ? "Uses pre-defined email addresses"
                            : "Asks caller for their email address"}
                        </p>
                      </div>
                    <Switch
                      id="emailUseContactEmail"
                      name="emailUseContactEmail"
                      checked={pathwayConfig.toolConnection.emailUseContactEmail || false}
                      onCheckedChange={(checked) => updateToolConnection('emailUseContactEmail', checked)}
                      className="data-[state=checked]:bg-indigo-500"
                    />
                  </div>
                </div>
                {/* Email Address Input */}
                {pathwayConfig.toolConnection.emailUseContactEmail && (
                  <div className="space-y-1.5 sm:space-y-2 pt-3">
                     <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                         Recipients <span className="text-red-500 ml-0.5">*</span>
                      </h3>
                      <p className="text-[10px] sm:text-xs text-gray-500">
                         Add email addresses that will receive this email
                      </p>
                    <EmailAddressInput
                      addresses={pathwayConfig.toolConnection.emailAddresses || []}
                      onChange={(addresses) => updateToolConnection('emailAddresses', addresses)}
                      error={validationErrors?.emailAddresses}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Transfer Call Form - Updated handlers */}
            {pathwayConfig?.toolConnection?.actionType === 'transfer' && (
              <div className="space-y-4 sm:space-y-5">
                {/* Transfer Name */}
                <div className="space-y-1.5 sm:space-y-2">
                   <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                        <Building className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                        Transfer Name <span className="text-red-500 ml-0.5">*</span>
                     </h3>
                     <p className="text-[10px] sm:text-xs text-gray-500">
                        Name for this transfer (e.g., "Support Team", "Sales Department")
                     </p>
                   <Input
                     id="transferName"
                     name="transferName"
                     value={pathwayConfig.toolConnection.transferName || ''}
                     onChange={(e) => updateToolConnection('transferName', e.target.value)}
                     placeholder="e.g., Support Team, Sales"
                      className={cn(
                         "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                         validationErrors?.transferName && "border-red-300 focus:border-red-500 focus:ring-red-500"
                       )}
                   />
                </div>
                {/* Transfer Number */}
                <div className="space-y-1.5 sm:space-y-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                    <PhoneForwarded className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                    Transfer Number <span className="text-red-500 ml-0.5">*</span>
                  </h3>
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    Enter the phone number to forward calls to when triggered
                  </p>
                  <Input
                    id="transferNumber"
                    name="transferNumber"
                    value={pathwayConfig.toolConnection.transferNumber || ''}
                    onChange={(e) => updateToolConnection('transferNumber', e.target.value)}
                    placeholder="+1 (555) 555-5555"
                    className={cn(
                      "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                      validationErrors?.transferNumber && "border-red-300 focus:border-red-500 focus:ring-red-500"
                    )}
                  />
                </div>
                {/* Operating Hours (Start/End Time) */}
                <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                    <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                    Operating Hours <span className="text-red-500 ml-0.5">*</span>
                  </h3>
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    Set hours when this transfer is active
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div>
                      <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">Start Time</label>
                      <Input
                        type="time"
                        id="transferStartTime"
                        name="transferStartTime"
                        value={pathwayConfig.toolConnection.transferStartTime || '09:00'}
                        onChange={(e) => updateToolConnection('transferStartTime', e.target.value)}
                        className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">End Time</label>
                      <Input
                        type="time"
                        id="transferEndTime"
                        name="transferEndTime"
                        value={pathwayConfig.toolConnection.transferEndTime || '17:00'}
                        onChange={(e) => updateToolConnection('transferEndTime', e.target.value)}
                        className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>
                {/* Days of Week */}
                <div className="space-y-1.5 sm:space-y-2">
                   <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                      <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                      Days of Week <span className="text-red-500 ml-0.5">*</span>
                   </h3>
                   <p className="text-[10px] sm:text-xs text-gray-500">
                      Select days when this transfer is active
                   </p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const currentDays = pathwayConfig.toolConnection.transferDays || [0,1,2,3,4,5,6];
                          const newDays = currentDays.includes(index)
                            ? currentDays.filter(d => d !== index)
                            : [...currentDays, index].sort();
                          updateToolConnection('transferDays', newDays);
                        }}
                        className={cn(
                          "px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors",
                          pathwayConfig.toolConnection.transferDays?.includes(index)
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                        )}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Play Message Toggle */}
                 <div className="pt-3 sm:pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                     <div className="space-y-0.5 sm:space-y-1">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-700">Play Message</h4>
                        <p className="text-[10px] sm:text-xs text-gray-500">
                           Play a message before transferring the call
                        </p>
                      </div>
                    <Switch
                      id="playMessage"
                      name="playMessage"
                      checked={pathwayConfig.toolConnection.playMessage || false}
                      onCheckedChange={(checked) => updateToolConnection('playMessage', checked)}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>
                </div>
                {/* Transfer Message Textarea (conditional) */}
                {pathwayConfig.toolConnection.playMessage && (
                  <div className="space-y-1.5 sm:space-y-2">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                        Transfer Message <span className="text-red-500 ml-0.5">*</span>
                     </h3>
                     <p className="text-[10px] sm:text-xs text-gray-500">
                        This message will be played to the caller before transferring
                     </p>
                    <RichTextEditor
                      value={pathwayConfig.toolConnection.transferMessage || ''}
                      onChange={(htmlContent) => updateToolConnection('transferMessage', htmlContent)}
                      placeholder="Please hold while we transfer your call... (type '@' to mention questions)"
                      questions={allQuestionsForMentions}
                      className={cn(
                        "min-h-[70px] sm:min-h-[80px]",
                        validationErrors?.transferMessage && "!border-red-300 focus:!border-red-500 focus:!ring-red-500"
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            {/* API Form - Updated handlers */}
            {pathwayConfig?.toolConnection?.actionType === 'api' && (
              <div className="space-y-4 sm:space-y-5">
                {/* API Endpoint */}
                <div className="space-y-1.5 sm:space-y-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                      <Webhook className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-purple-500" />
                      API Endpoint <span className="text-red-500 ml-0.5">*</span>
                   </h3>
                   <p className="text-[10px] sm:text-xs text-gray-500">
                      Enter the URL of the API endpoint to call when triggered
                   </p>
                  <Input
                    id="apiEndpoint"
                    name="apiEndpoint"
                    value={pathwayConfig.toolConnection.apiEndpoint || ''}
                    onChange={(e) => updateToolConnection('apiEndpoint', e.target.value)}
                    placeholder="https://api.example.com/webhook"
                    className={cn(
                      "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                      validationErrors?.apiEndpoint && "border-red-300 focus:border-red-500 focus:ring-red-500"
                    )}
                  />
                </div>
                {/* Request Method */}
                 <div className="space-y-1.5 sm:space-y-2">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                      <Code className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-purple-500" />
                      Request Method
                   </h3>
                  <div className="flex space-x-2">
                    {['GET', 'POST', 'PUT', 'DELETE'].map(method => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => updateToolConnection('apiMethod', method)}
                        className={cn(
                          "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                          (pathwayConfig.toolConnection.apiMethod || 'POST') === method
                            ? "bg-purple-100 text-purple-800 border border-purple-200"
                            : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                        )}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Headers Input */}
                <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                      <ListFilter className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-purple-500" />
                      Headers (Optional)
                   </h3>
                   <p className="text-[10px] sm:text-xs text-gray-500">
                      Add any headers to include with the request
                   </p>
                  <ApiHeadersInput
                    headers={pathwayConfig.toolConnection.apiHeaders || {}}
                    onChange={(headers) => updateToolConnection('apiHeaders', headers)}
                  />
                </div>
                {/* Request Body */}
                 <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                      Request Body {(pathwayConfig.toolConnection.apiMethod || 'POST') !== 'GET' && (<span className="text-red-500 ml-0.5">*</span>)}
                   </h3>
                   <p className="text-[10px] sm:text-xs text-gray-500">
                      JSON payload to send with the request
                   </p>
                  <RichTextEditor
                    value={pathwayConfig.toolConnection.apiBody || ''}
                    onChange={(htmlContent) => updateToolConnection('apiBody', htmlContent)}
                    placeholder='{ "key": "value" } (type @ to mention questions)'
                    questions={allQuestionsForMentions}
                    className={cn(
                      "min-h-[120px] sm:min-h-[140px] font-mono", // Retain font-mono for API body
                      validationErrors?.apiBody && "!border-red-300 focus:!border-red-500 focus:!ring-red-500"
                    )}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </TabsContent>

        <TabsContent value="locations" forceMount={true} className={activeTab !== 'locations' ? 'hidden' : ''}>
              <LocationsTab 
                  pathwayConfig={pathwayConfig} 
                  setPathwayConfig={setPathwayConfig} 
                  showLocationVariants={showLocationVariants} // Pass variant flag
                  handleFormChangeWithVariants={handleFormChangeWithVariants} // Pass variant handler
                  setHasUnsavedChanges={setHasUnsavedChanges}
              />
            </TabsContent>
          </div>
      </Tabs>
  );
};

export default OrderPathwayBuilder; 